import { useMemo } from 'react';

import { getDashboardsDrawerPortalTarget } from '../../../shared/utils';

/**
 * SlideOut wiring for the widget detail panel (CT-5188 / DS-4).
 *
 * Adapted from `aims-os-governance/src/domains/truth-plane/components/
 * PreviewDrawers/previewSlideOutShared.js`. Duplicated, not imported: apps never
 * pull from `@aimsos/governance`.
 *
 * ## The portal
 *
 * The panel mounts into `#dashboards-overlay-root`, not `document.body`.
 *
 * An earlier revision of this file argued the opposite — that this app had no
 * overlay root, so `body` was the honest default. That was true and it was the
 * wrong conclusion: both studios mount such a root precisely so a panel's
 * backdrop stays inside the content column, and on `body` `SlideOut` goes
 * `fixed inset-0` and dims the shell's top bar and module nav too. An overlay
 * opened from a list in this app is about this app; saying the whole product
 * is blocked is a claim the panel has no business making. The root is mounted
 * in `WidgetsApp` now and this follows the convention.
 *
 * `!pointer-events-auto` on both layers is not cargo cult here either — it is
 * what undoes the root's `pointer-events-none`, which exists so an empty root
 * does not swallow clicks on the page beneath it.
 *
 * ## The other two differences from the governance original
 *
 *   - **`noPadding`.** The slot below supplies its own rows.
 *
 * ## The width
 *
 * `M` (635px), same as the governance original. This started at `S` (420px)
 * because the prototype's panel is narrow, and `S` could not hold the footer:
 * three labelled buttons need 414px against 370px of content width, so the
 * row wrapped and left Delete stranded on a line of its own.
 *
 * Spanish is what settles it rather than English. "Editar configuración
 * completa" and "Agregar a un dashboard" are half again as long as their
 * English counterparts, and a width chosen against English would wrap the
 * moment the locale changed — which is the same bug, found later and by
 * someone else.
 *
 * The preview is the other beneficiary: it offers Small / Medium / Large and
 * had 372px to render all three in.
 *
 * `panelZClassName` still needs `z-[101]`: passing this prop replaces
 * `SlideOut`'s own default entirely rather than extending it, so dropping the
 * z-index utility here (keeping only the height overrides) left the panel at
 * `z-index: auto` — *below* the backdrop's `z-[100]`. The backdrop then
 * physically covered every control in the panel, so any click anywhere in the
 * panel (Delete, Edit, the size selector, even Close) actually landed on the
 * backdrop's own click-to-close handler instead.
 */

/**
 * The overlay root to portal into, resolved once per mount.
 *
 * A hook rather than a constant because the element does not exist when this
 * module is evaluated — it is rendered by `WidgetsApp`.
 */
export function useWidgetPreviewSlideOutPortal() {
  return useMemo(
    () => (typeof document !== 'undefined' ? getDashboardsDrawerPortalTarget() : null),
    [],
  );
}

/** Shared {@link SlideOut} props for the widget detail panel. */
export const WIDGET_PREVIEW_SLIDEOUT_PROPS = {
  type: 'full-slot',
  size: 'M',
  noPadding: true,
  backdropZClassName: 'z-[100] !pointer-events-auto',
  panelZClassName: 'z-[101] !pointer-events-auto !h-full !max-h-full !min-h-0',
};

/**
 * Full-height slot inside the panel: header row, scrollable body, footer row.
 * The middle `minmax(0,1fr)` is what lets the body scroll while the header and
 * the actions stay put.
 */
export const WIDGET_PREVIEW_SLOT_CLASS =
  'grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden';

/** Scrollable body between the header and the footer actions. */
export const WIDGET_PREVIEW_SCROLL_CLASS = 'scrollbar-aimsos-auto min-h-0 overflow-y-auto';
